/**
 * Brand Campaign ROI — relational report.
 *
 * Per-campaign breakdown: budget vs earnings paid out, fulfillment count,
 * impressions/clicks/views, derived budget utilization %, CTR, budget per
 * click. Useful for ad-sales to show brands which campaigns spent
 * efficiently and which didn't.
 */
import React, { useState } from "react";
import { tokensV2 as T } from "./tokens.v2";
import { useBrandCampaignRoi } from "./useInsights.v2";

function formatINR(n) {
  if (n == null) return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  if (num >= 1e7) return "Rs " + (num / 1e7).toFixed(2) + "Cr";
  if (num >= 1e5) return "Rs " + (num / 1e5).toFixed(2) + "L";
  if (num >= 1e3) return "Rs " + (num / 1e3).toFixed(1) + "K";
  return "Rs " + Math.round(num);
}
function formatNum(n) {
  if (n == null) return "—";
  return Number(n).toLocaleString("en-IN");
}
function todayStr() { return new Date().toISOString().slice(0, 10); }
function yearAgoStr() { const d = new Date(); d.setFullYear(d.getFullYear() - 1); return d.toISOString().slice(0, 10); }

export function BrandCampaignRoiReport() {
  const [filters, setFilters] = useState({ from: yearAgoStr(), to: todayStr(), limit: 50 });
  const { data, loading, error } = useBrandCampaignRoi(filters);

  const onExport = () => {
    if (!data?.rows) return;
    const cols = ["title", "brandName", "campaignType", "status", "budget", "creatorEarnings", "creatorsPaid", "totalImpressions", "totalClicks", "totalViews", "budgetUtilizationPct", "clickThroughRate", "budgetPerClick", "avgPerCreator"];
    const escape = (v) => v == null ? "" : (/[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v));
    const header = `# Brand Campaign ROI · ${filters.from} → ${filters.to}\n` + cols.join(",");
    const lines = data.rows.map(r => cols.map(c => escape(r[c])).join(","));
    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `brand-campaign-roi-${filters.from}-to-${filters.to}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div style={{ background: T.surface.card, border: `1px solid ${T.border.subtle}`, borderRadius: 14, padding: 16, fontFamily: T.font.sans }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.text.primary }}>Brand Campaign ROI</div>
          <div style={{ fontSize: 12, color: T.text.muted, marginTop: 2 }}>
            Window: {filters.from} → {filters.to} · {data?.totals?.rowCount ?? "—"} campaigns
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ fontSize: 11, color: T.text.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>From</label>
          <input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })}
                 style={{ padding: "6px 8px", border: `1px solid ${T.border.subtle}`, borderRadius: 6, fontSize: 12 }} />
          <label style={{ fontSize: 11, color: T.text.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>To</label>
          <input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })}
                 style={{ padding: "6px 8px", border: `1px solid ${T.border.subtle}`, borderRadius: 6, fontSize: 12 }} />
          <button type="button" onClick={onExport} disabled={!data?.rows?.length}
                  style={{ padding: "6px 14px", background: T.brand.base, color: "#FFFFFF", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: data?.rows?.length ? "pointer" : "not-allowed", opacity: data?.rows?.length ? 1 : 0.5 }}>Export CSV</button>
        </div>
      </div>

      {error && (
        <div style={{ padding: "10px 14px", background: T.status.warnBg, border: `1px solid ${T.status.warn}`, borderRadius: 8, fontSize: 12, marginBottom: 10 }}>
          <strong>Could not load report:</strong> {error}
        </div>
      )}
      {loading && !data && (
        <div style={{ padding: 20, textAlign: "center", color: T.text.muted, fontSize: 13 }}>Loading…</div>
      )}

      {data?.rows && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font.sans, fontSize: 12, fontVariantNumeric: "tabular-nums" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${T.border.subtle}`, fontSize: 10, color: T.text.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                <th style={{ textAlign: "left",  padding: "8px", fontWeight: 700 }}>Campaign</th>
                <th style={{ textAlign: "left",  padding: "8px", fontWeight: 700 }}>Brand</th>
                <th style={{ textAlign: "left",  padding: "8px", fontWeight: 700 }}>Type</th>
                <th style={{ textAlign: "left",  padding: "8px", fontWeight: 700 }}>Status</th>
                <th style={{ textAlign: "right", padding: "8px", fontWeight: 700 }}>Budget</th>
                <th style={{ textAlign: "right", padding: "8px", fontWeight: 700 }}>Earnings paid</th>
                <th style={{ textAlign: "right", padding: "8px", fontWeight: 700 }}>Util %</th>
                <th style={{ textAlign: "right", padding: "8px", fontWeight: 700 }}>Creators</th>
                <th style={{ textAlign: "right", padding: "8px", fontWeight: 700 }}>Impressions</th>
                <th style={{ textAlign: "right", padding: "8px", fontWeight: 700 }}>Clicks</th>
                <th style={{ textAlign: "right", padding: "8px", fontWeight: 700 }}>CTR</th>
                <th style={{ textAlign: "right", padding: "8px", fontWeight: 700 }}>₹/click</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 ? (
                <tr><td colSpan={12} style={{ textAlign: "center", padding: 20, color: T.text.muted }}>No campaigns in window.</td></tr>
              ) : data.rows.map((r, i) => (
                <tr key={r.campaignId} style={{ borderBottom: i < data.rows.length - 1 ? `1px solid ${T.border.subtle}` : "none" }}>
                  <td style={{ padding: "8px", fontWeight: 600, color: T.text.primary, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.title || `#${r.campaignId}`}</td>
                  <td style={{ padding: "8px", color: T.text.secondary }}>{r.brandName}</td>
                  <td style={{ padding: "8px", color: T.text.muted }}>{r.campaignType || "—"}</td>
                  <td style={{ padding: "8px", color: T.text.muted }}>{r.status || "—"}</td>
                  <td style={{ padding: "8px", textAlign: "right", fontWeight: 700, color: T.text.primary }}>{formatINR(r.budget)}</td>
                  <td style={{ padding: "8px", textAlign: "right", color: T.text.secondary }}>{formatINR(r.creatorEarnings)}</td>
                  <td style={{ padding: "8px", textAlign: "right", fontWeight: 700, color: r.budgetUtilizationPct >= 80 ? "#0E7B47" : r.budgetUtilizationPct >= 30 ? "#FC8019" : "#A82A2A" }}>{r.budgetUtilizationPct.toFixed(1)}%</td>
                  <td style={{ padding: "8px", textAlign: "right", color: T.text.muted }}>{formatNum(r.creatorsPaid)}</td>
                  <td style={{ padding: "8px", textAlign: "right", color: T.text.muted }}>{formatNum(r.totalImpressions)}</td>
                  <td style={{ padding: "8px", textAlign: "right", color: T.text.muted }}>{formatNum(r.totalClicks)}</td>
                  <td style={{ padding: "8px", textAlign: "right", color: T.text.secondary }}>{r.clickThroughRate.toFixed(2)}%</td>
                  <td style={{ padding: "8px", textAlign: "right", color: T.text.secondary }}>{formatINR(r.budgetPerClick)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
